import type { Tab } from './index';
import { esc } from '../utils';
import { fetchSheet } from '../worker';
import { writeToSheet } from '../sheets';
import { showToast } from '../toast';

const LS_KEY = 'meridian-notes';

interface LocalNote {
  title: string;
  body:  string;
  date:  string;
}

interface SheetNote {
  _id?:       string | number;
  title?:     string;
  content?:   string;
  ticker?:    string;
  timestamp?: string;
}

export class NotesTab implements Tab {
  readonly id = 'notes';

  init(): void {
    this.el('notes-refresh-btn').addEventListener('click', () => this.loadSheetNotes());
    this.el('notes-save-btn').addEventListener('click',    () => this.save());
    this.el('notes-clear-btn').addEventListener('click',   () => this.clearAll());

    // Enter in title field saves note
    (this.el('note-title') as HTMLInputElement).addEventListener('keydown', e => {
      if (e.key === 'Enter') (this.el('note-body') as HTMLTextAreaElement).focus();
    });

    // Delete local notes via event delegation
    this.el('notes-list').addEventListener('click', e => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-delete-idx]');
      if (btn) this.deleteNote(Number(btn.dataset.deleteIdx));
    });

    // Delete sheet notes via event delegation
    this.el('sheet-notes-list').addEventListener('click', e => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-delete-sheet-id]');
      if (btn) this.deleteSheetNote(btn.dataset.deleteSheetId ?? '');
    });

    this.renderLocal();
  }

  render(): void {
    this.loadSheetNotes();
    this.renderLocal();
  }

  // ── Sheet Notes ───────────────────────────────────────────────────────────

  private async loadSheetNotes(): Promise<void> {
    const container = this.el('sheet-notes-list');
    container.innerHTML = '<div style="color:var(--text3);font-size:11px;letter-spacing:0.1em;padding:12px 0;">LOADING…</div>';
    try {
      const data = await fetchSheet<SheetNote>('Notes');
      const rows = data.rows ?? [];
      if (!rows.length) {
        container.innerHTML = '<div style="color:var(--text3);font-size:12px;padding:12px 0;">No notes in Sheet yet — add some via the AI assistant or Deep Research tab.</div>';
        return;
      }
      const sorted = [...rows].sort((a, b) =>
        (b.timestamp ?? '') > (a.timestamp ?? '') ? 1 : -1,
      );
      container.innerHTML = sorted.map(r => this.buildSheetCard(r)).join('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      container.innerHTML =
        `<div style="padding:12px 0;">` +
        `<span style="color:var(--red);font-size:12px;">Could not load Sheet notes.</span> ` +
        `<span style="color:var(--text3);font-size:11px;">${esc(msg)}</span></div>`;
    }
  }

  private buildSheetCard(r: SheetNote): string {
    const ticker  = r.ticker ? `<span class="ticker-badge" style="font-size:10px;padding:1px 6px;margin-left:6px;">${esc(r.ticker)}</span>` : '';
    const dateStr = r.timestamp ? (() => { try { return new Date(r.timestamp).toLocaleString(); } catch { return esc(r.timestamp ?? ''); } })() : '';
    const delBtn  = r._id != null
      ? `<button data-delete-sheet-id="${esc(String(r._id))}" title="Delete note" style="background:none;border:none;color:var(--text3);cursor:pointer;font-size:12px;font-family:var(--mono);padding:0 2px;margin-left:8px;transition:color 0.15s;" onmouseover="this.style.color='var(--red)'" onmouseout="this.style.color='var(--text3)'">✕</button>`
      : '';
    return `<div style="background:var(--panel);border:1px solid var(--border);border-radius:4px;padding:14px 16px;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
        <div><strong style="color:var(--accent);font-size:13px;">${esc(r.title ?? 'Untitled')}</strong>${ticker}</div>
        <div style="display:flex;align-items:center;gap:4px;">
          <span style="color:var(--text3);font-size:10px;">${dateStr}</span>
          ${delBtn}
        </div>
      </div>
      <div style="color:var(--text2);font-size:12px;white-space:pre-wrap;line-height:1.6;">${esc(r.content ?? '')}</div>
    </div>`;
  }

  private async deleteSheetNote(id: string): Promise<void> {
    if (!id || !confirm('Delete this note from Google Sheets?')) return;
    const ok = await writeToSheet('delete', 'Notes', { _id: id });
    if (ok) {
      showToast('Note deleted');
      await this.loadSheetNotes();
    }
  }

  // ── Local Quick Notes ─────────────────────────────────────────────────────

  private save(): void {
    const titleEl = this.el('note-title') as HTMLInputElement;
    const bodyEl  = this.el('note-body')  as HTMLTextAreaElement;
    if (!bodyEl.value.trim()) return;
    const notes = this.getLocal();
    notes.unshift({ title: titleEl.value || 'Untitled', body: bodyEl.value, date: new Date().toLocaleString() });
    this.setLocal(notes);
    titleEl.value = '';
    bodyEl.value  = '';
    this.renderLocal();
  }

  private clearAll(): void {
    if (confirm('Clear all local notes?')) {
      localStorage.removeItem(LS_KEY);
      this.renderLocal();
    }
  }

  private deleteNote(i: number): void {
    const notes = this.getLocal();
    notes.splice(i, 1);
    this.setLocal(notes);
    this.renderLocal();
  }

  private renderLocal(): void {
    const notes     = this.getLocal();
    const container = this.el('notes-list');
    if (!notes.length) {
      container.innerHTML = '<div style="color:var(--text3);font-size:12px;padding:20px 0;">No local notes saved yet.</div>';
      return;
    }
    container.innerHTML = notes.map((n, i) => `
      <div style="background:var(--panel);border:1px solid var(--border);border-radius:4px;padding:14px 16px;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
          <strong style="color:var(--accent);font-size:13px;">${esc(n.title)}</strong>
          <div style="display:flex;gap:8px;align-items:center;">
            <span style="color:var(--text3);font-size:10px;">${esc(n.date)}</span>
            <button data-delete-idx="${i}" style="background:none;border:none;color:var(--red);cursor:pointer;font-size:12px;font-family:var(--mono);">✕</button>
          </div>
        </div>
        <div style="color:var(--text2);font-size:12px;white-space:pre-wrap;line-height:1.6;">${esc(n.body)}</div>
      </div>`).join('');
  }

  private getLocal(): LocalNote[] {
    try { return JSON.parse(localStorage.getItem(LS_KEY) ?? '[]') as LocalNote[]; }
    catch { return []; }
  }

  private setLocal(notes: LocalNote[]): void {
    localStorage.setItem(LS_KEY, JSON.stringify(notes));
  }

  private el(id: string): HTMLElement {
    return document.getElementById(id) as HTMLElement;
  }
}
